import { Card, Colors, FOCUS_RING, Heading, Icon, Pills, Text } from '@amelio/ui-library';

import { CARD_STACK_GAP, MEETINGS_TITLE } from './meetingStyles';

import type { ThematicMeetingStats } from '@/types/thematic-meetings';
import type { PillsVariant } from '@amelio/ui-library';

import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';

interface QuickStatsProps {
    stats: ThematicMeetingStats | undefined;
    isLoading: boolean;
    onOpenOverdue: () => void;
}

interface StatValueProps {
    /** Undefined until the stats have answered: a count that has not arrived is never a zero. */
    value: number | undefined;
    isLoading: boolean;
    variant: PillsVariant;
}

const StatValue = ({ value, isLoading, variant }: StatValueProps) => {
    const { t } = useAmelioTranslations();

    if (value !== undefined) {
        return (
            <Pills
                variant={variant}
                size={'sm'}
            >
                {value}
            </Pills>
        );
    }

    if (isLoading) {
        return (
            <Skeleton>
                <Bone
                    color={Colors.BASE_100}
                    className={'h-6 w-8 rounded-full'}
                />
            </Skeleton>
        );
    }

    // The stats did not answer: a neutral mark on screen, never read out as a value, and a word for
    // screen readers in its place.
    return (
        <span>
            <span aria-hidden={'true'}>
                <Text color={Colors.BASE_500}>—</Text>
            </span>

            <span className={'sr-only'}>{t('thematicMeetings.stats.unavailable')}</span>
        </span>
    );
};

interface StatRowProps extends StatValueProps {
    label: string;
    dataTestId: string;
}

const StatRow = ({ label, dataTestId, ...valueProps }: StatRowProps) => (
    <div
        className={'flex h-8 items-center justify-between'}
        data-testid={dataTestId}
    >
        <Text color={Colors.BASE_500}>{label}</Text>

        <StatValue {...valueProps} />
    </div>
);

export const QuickStats = ({ stats, isLoading, onOpenOverdue }: QuickStatsProps) => {
    const { t } = useAmelioTranslations();
    // The employee list is capped by the backend; the total is the "how many" a manager should see.
    const overdueCount = stats?.overdueEmployeesTotalCount ?? 0;

    return (
        <Card
            padding={'none'}
            borderRadius={'xl'}
            glass={'sm'}
            className={'p-4'}
            dataTestId={'quick-stats-card'}
        >
            <Heading
                level={4}
                align={'center'}
                color={Colors.NAVY}
                className={MEETINGS_TITLE}
            >
                {t('thematicMeetings.stats.title')}
            </Heading>

            <Card
                variant={'flat'}
                bgColor={'bg-white/90'}
                borderRadius={'lg'}
                className={'mt-4'}
            >
                <div className={`flex flex-col ${CARD_STACK_GAP}`}>
                    <StatRow
                        label={t('thematicMeetings.stats.planned')}
                        value={stats?.plannedCount}
                        isLoading={isLoading}
                        variant={'primary'}
                        dataTestId={'quick-stats-planned'}
                    />

                    <StatRow
                        label={t('thematicMeetings.stats.thisMonth')}
                        value={stats?.completedThisMonthCount}
                        isLoading={isLoading}
                        variant={'default'}
                        dataTestId={'quick-stats-this-month'}
                    />

                    <StatRow
                        label={t('thematicMeetings.stats.completed')}
                        value={stats?.completedCount}
                        isLoading={isLoading}
                        variant={'default'}
                        dataTestId={'quick-stats-completed'}
                    />

                    {/* Only once there is something overdue to report, never a disabled button. */}
                    {overdueCount > 0 && (
                        <button
                            type={'button'}
                            onClick={onOpenOverdue}
                            // Composed from the row's own visible text (label + pill count)
                            // so the accessible name contains what's on screen (WCAG 2.5.3),
                            // instead of a separately-worded translation string.
                            aria-label={`${t('thematicMeetings.stats.overdueLabel')} (${overdueCount})`}
                            className={`${FOCUS_RING} flex items-center justify-between gap-2 text-left`}
                            data-testid={'quick-stats-overdue-button'}
                        >
                            <span className={'flex items-center gap-1.5'}>
                                <Icon
                                    name={'essential-danger'}
                                    size={'sm'}
                                    color={Colors.WARNING_800}
                                    ariaHidden
                                />

                                <Text
                                    color={Colors.PRIMARY_800}
                                    weight={'medium'}
                                >
                                    {t('thematicMeetings.stats.overdueLabel')}
                                </Text>
                            </span>

                            <Pills
                                variant={'draftTinted'}
                                size={'sm'}
                            >
                                {overdueCount}
                            </Pills>
                        </button>
                    )}
                </div>
            </Card>
        </Card>
    );
};
